import * as XLSX from "xlsx-js-style";
import { SERVER_URL } from "../api";

const HEADERS_GENERAL = [
  "Fecha",
  "Nombre",
  "Email",
  "Teléfono",
  "Asunto",
  "Mensaje",
  "Estado",
];

const HEADERS_ENVIO = [
  "Fecha",
  "Nombre",
  "Email",
  "DNI",
  "Titulación",
  "Situación laboral",
  "Centro de trabajo",
  "PDF",
  "Estado",
];

const BORDER_GRID = {
  top: { style: "thin", color: { rgb: "BFBFBF" } },
  bottom: { style: "thin", color: { rgb: "BFBFBF" } },
  left: { style: "thin", color: { rgb: "BFBFBF" } },
  right: { style: "thin", color: { rgb: "BFBFBF" } },
};

function str(v) {
  if (v == null || v === undefined) return "";
  return String(v).trim();
}

function formatDetailDate(value) {
  if (value == null || value === "") return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
}

function mediaUrl(path) {
  if (!path) return "";
  const p = String(path).trim().replace(/^\//, "");
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  return `${SERVER_URL}/${p}`;
}

function resolveDocumentoPdfUrl(documentoPdf) {
  const p = String(documentoPdf || "").trim();
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  return mediaUrl(p);
}

function pdfCell(documentoPdf) {
  const url = resolveDocumentoPdfUrl(documentoPdf);
  if (!url) return "";
  return {
    t: "s",
    v: "Descargar PDF",
    l: { Target: url, Tooltip: "Abrir documento PDF" },
  };
}

function rowFromContacto(r, variant) {
  if (variant === "envio") {
    return [
      formatDetailDate(r.createdAt),
      str(r.name),
      str(r.email),
      str(r.dni),
      str(r.titulacion),
      str(r.situacionLaboral),
      str(r.centroTrabajo),
      pdfCell(r.documentoPdf),
      str(r.gestionado),
    ];
  }
  return [
    formatDetailDate(r.createdAt),
    str(r.name),
    str(r.email),
    str(r.tel),
    str(r.asunto),
    str(r.mensaje),
    str(r.gestionado),
  ];
}

function getHeaders(variant) {
  return variant === "envio" ? HEADERS_ENVIO : HEADERS_GENERAL;
}

function getColWidths(variant) {
  if (variant === "envio") {
    return [
      { wch: 18 },
      { wch: 22 },
      { wch: 36 },
      { wch: 14 },
      { wch: 18 },
      { wch: 20 },
      { wch: 24 },
      { wch: 16 },
      { wch: 14 },
    ];
  }
  return [
    { wch: 18 },
    { wch: 22 },
    { wch: 36 },
    { wch: 16 },
    { wch: 28 },
    { wch: 48 },
    { wch: 14 },
  ];
}

function dataColumnWrapText(col, variant) {
  if (variant === "envio") return [1, 2].includes(col);
  return [1, 2, 4, 5].includes(col);
}

function applySheetVisuals(ws, { planRow, headerRow, dataStart, dataEnd, lastCol, variant }) {
  ws["!cols"] = getColWidths(variant);
  ws["!merges"] = [{ s: { r: planRow, c: 0 }, e: { r: planRow, c: lastCol } }];

  const planStyle = {
    font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
    fill: { patternType: "solid", fgColor: { rgb: "E97132" } },
    alignment: { horizontal: "left", vertical: "center", wrapText: true },
    border: BORDER_GRID,
  };

  const headerStyle = {
    font: { bold: true, sz: 11, color: { rgb: "1F1F1F" } },
    fill: { patternType: "solid", fgColor: { rgb: "D6DCE5" } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: BORDER_GRID,
  };

  const rowHeights = [];
  rowHeights[planRow] = { hpt: 32 };

  const planRef = XLSX.utils.encode_cell({ r: planRow, c: 0 });
  if (ws[planRef]) ws[planRef].s = { ...planStyle };

  for (let c = 0; c <= lastCol; c++) {
    const ref = XLSX.utils.encode_cell({ r: headerRow, c });
    if (ws[ref]) ws[ref].s = { ...headerStyle };
  }

  const pdfCol = variant === "envio" ? 7 : -1;
  let zebra = false;
  if (dataEnd >= dataStart) {
    for (let r = dataStart; r <= dataEnd; r++) {
      rowHeights[r] = { hpt: 22 };
      for (let c = 0; c <= lastCol; c++) {
        const ref = XLSX.utils.encode_cell({ r, c });
        const cell = ws[ref];
        if (!cell) continue;
        const wrap = dataColumnWrapText(c, variant);
        const zebraFill = zebra
          ? { patternType: "solid", fgColor: { rgb: "F5F6F8" } }
          : { patternType: "solid", fgColor: { rgb: "FFFFFF" } };
        cell.s = {
          font: { sz: 11, color: { rgb: "222222" } },
          fill: zebraFill,
          alignment: {
            vertical: "top",
            wrapText: wrap,
            horizontal: c === pdfCol ? "center" : "left",
          },
          border: BORDER_GRID,
        };
      }
      zebra = !zebra;
    }
  }

  ws["!rows"] = rowHeights;
}

/**
 * @param {{ title: string, rows: object[], variant: 'envio'|'general' }} params
 * @returns {import('xlsx').WorkSheet|null}
 */
export function buildContactosWorksheet({ title, rows, variant }) {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  const headers = getHeaders(variant);
  const lastCol = headers.length - 1;
  const titleLine = `${title} — ${rows.length} envío(s)`;
  const aoa = [[titleLine], headers, ...rows.map((r) => rowFromContacto(r, variant))];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  applySheetVisuals(ws, {
    planRow: 0,
    headerRow: 1,
    dataStart: 2,
    dataEnd: 1 + rows.length,
    lastCol,
    variant,
  });
  return ws;
}

function excelDateStamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

/**
 * @param {{ year?: number|string, sectionKey: string, title: string, rows: object[] }} params
 */
export function exportCongresoContactosExcel({ year, sectionKey, title, rows }) {
  const variant = sectionKey === "envio" ? "envio" : "general";
  const ws = buildContactosWorksheet({ title, rows, variant });
  if (!ws) return false;

  const wb = XLSX.utils.book_new();
  const sheetName = String(title || sectionKey).slice(0, 31);
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  const y = year != null && year !== "" ? String(year) : "edicion";
  const slug = String(sectionKey || "contactos").replace(/[^a-z0-9-]/gi, "-");
  XLSX.writeFile(wb, `contactos-congreso-${slug}-${y}-${excelDateStamp()}.xlsx`);
  return true;
}
